import type { ServiceLayer } from '../types/gis';
import { getLayerSlug } from '../utils/routeUtils';

export interface GuideFaqAction {
  label_en: string;
  label_ta: string;
  url: string;
}

export interface GuideFaqItem {
  question: string;
  answer: string;
  question_ta?: string;
  answer_ta?: string;
  actions?: GuideFaqAction[];
}

export interface ModuleGuideContent {
  layer: ServiceLayer;
  title: string;
  summary: string;
  inDepthOverview: string;
  whyItMatters: string[];
  howToUse: string[];
  useCases: string[];
  faq: GuideFaqItem[];
  ctaText?: string;
  alertTitle?: string;
  alertText?: string;
}

export const MODULE_GUIDES: Record<ServiceLayer, ModuleGuideContent> = {
  PINCODE: {
    layer: 'PINCODE',
    title: 'Post Office & Pincode Lookup in Tamil Nadu',
    summary: 'Find precise 6-digit postal code boundaries, identify local post office branches, and verify official delivery service availability with live map context.',
    inDepthOverview: 'The Tamil Nadu Pincode map provides a comprehensive spatial index of every 6-digit postal code region across the state. Understanding postal boundaries is critical for logistical coordination, address verification, and governance compliance. NammaMap parses complex TopoJSON spatial data to map the exact polygon reach of each PIN code, ensuring users can instantly distinguish which village or ward falls into which official delivery cycle. Beyond simple code lookups, this module allows citizens to locate the physical branch responsible for their jurisdiction, categorizing non-delivery sub-offices and primary head-offices.',
    whyItMatters: [
      'Confirms whether an exact street address falls inside a specific pincode polygon.',
      'Helps citizens identify which delivery or non-delivery post office physically services their home.',
      'Supports businesses with location validation for KYC, shipping logistics, and official compliance.',
      'Eliminates confusion between overlapping village names shared across adjacent postal regions.'
    ],
    howToUse: [
      'Open the Post Offices module and enter a 6-digit code or area name into the search bar.',
      'Click any colored boundary on the map to highlight the entire coverage zone.',
      'Inspect the results card to view office metadata, status, and district classification.',
      'Copy and share the deep-link to verify a delivery zone for team coordination.'
    ],
    useCases: [
      'Formal address validation for passports, bank accounts, and shipping.',
      'Logistics planning to determine optimal delivery route perimeters.',
      'Identifying specific branch classification (Head Office vs Sub-Office).'
    ],
    faq: [
      {
        question: 'Can I search directly by the 6-digit pincode?',
        answer: 'Yes. Simply type the full 6 digits into the master search bar to instantly jump to the boundary and view its linked postal center details.'
      },
      {
        question: 'How accurate are the pincode boundaries on this map?',
        answer: 'The boundaries are derived from verified spatial data models; however, always verify final coordinates with the Department of Posts for critical regulatory filings.'
      },
      {
        question: 'Will the map show whether an office offers delivery services?',
        answer: 'Yes. The metadata indicates the office classification type, enabling you to know if a branch handles final-mile parcel deliveries or standard transactions.'
      },
      {
        question: 'Can I find a pincode using just a village name?',
        answer: 'Absolutely. Searching by village name will produce a dropdown of matching locations along with their assigned codes for quick identification.'
      }
    ],
    ctaText: 'Search by Pincode / Post Office',
    alertTitle: 'Got a Pincode or Area Name?',
    alertText: 'Click the Search by Pincode button above to open our interactive map and find the exact delivery jurisdiction instantly.'
  },
  PDS: {
    layer: 'PDS',
    title: 'Find My Ration Shop in Tamil Nadu: District-wise PDS Locator & Map',
    summary: 'How to find your ration shop in any district of Tamil Nadu. Use our map to search by shop number/code, locate fair price shops (FPS), and find exact addresses.',
    inDepthOverview: 'The Public Distribution System (PDS) is the backbone of citizen welfare. If you are wondering "how to find my ration shop in Chennai, Coimbatore, or Madurai," the NammaMap PDS module is the definitive solution. Identifying your designated Fair Price Shop (Ration Shop) requires knowledge of precise neighborhood coverage zones. This module simplifies the process by clustering thousands of verified shops across every district into a fast, searchable interface. Whether you are tracking your newly issued Family Card mapping or verifying coverage densities in a growing suburb, this mapping portal delivers point-level precision.',
    whyItMatters: [
      'Provides rapid identification of physical ration shops servicing local communities.',
      'Allows support organizations and citizens to monitor network-wide service access.',
      'Speeds up the process of locating assigned fair price shop numbers for new residents.',
      'Enhances transparency surrounding essential resource distribution centers.'
    ],
    howToUse: [
      'Toggle to the Ration Shops layer and zoom into your neighborhood or search by area.',
      'Notice the localized pin clusters indicating high densities of distribution nodes.',
      'Click a specific marker to unlock full name and locality metadata for that unit.',
      'Use sharing links to direct colleagues or community members to the specific facility.'
    ],
    useCases: [
      'Citizen grievance routing when reporting issues to Civil Supplies departments.',
      'Locating nearby alternative shops during infrastructure outages.',
      'Assisting new cardholders in completing their assigned shop lookup process.'
    ],
    faq: [
      {
        question: 'How do I find my ration shop address using a shop number or code?',
        question_ta: 'கடை எண் அல்லது குறியீட்டைப் பயன்படுத்தி எனது நியாய விலைக் கடையின் முகவரியைக் கண்டுபிடிப்பது எப்படி?',
        answer: 'Use the NammaMap interactive PDS locator. Enter your 9-character shop code (e.g., 01AA001PN), area name, or 6-digit pincode into the search bar to locate the exact Fair Price Shop, view its coverage zone, and get direct map navigation.',
        answer_ta: 'NammaMap வரைபட தேடுபொறியைப் பயன்படுத்தவும். உங்கள் 9 இலக்க கடை குறியீடு (எ.கா. 01AA001PN), பகுதி பெயர் அல்லது 6 இலக்க அஞ்சல் குறியீட்டை உள்ளிட்டு உங்கள் நியாய விலைக் கடையின் இருப்பிடம் மற்றும் வரைபட வழிகாட்டுதலைப் பெறலாம்.',
        actions: [
          { label_en: 'Search on NammaMap PDS Locator', label_ta: 'ரேஷன் கடை வரைபடத்தில் தேட', url: 'https://nammamap.in/pds' }
        ]
      },
      {
        question: 'How do I apply for a new Smart Ration Card in Tamil Nadu?',
        question_ta: 'புதிய ஸ்மார்ட் குடும்ப அட்டைக்கு எவ்வாறு விண்ணப்பிப்பது?',
        answer: 'You can apply online via the official TNPDS portal (under Smart Card Services -> Apply New Smart Card) or at any local e-Seva center. Required documents include Aadhaar cards of all family members, proof of residence (electricity bill, gas receipt, or rental deed), and a passport-size photo of the head of family.',
        answer_ta: 'புதிய ஸ்மார்ட் குடும்ப அட்டைக்கு TNPDS இணையதளத்தில் "ஸ்மார்ட் கார்டு சேவைகள்" -> "புதிய ஸ்மார்ட் கார்டு விண்ணப்பிக்க" என்ற பகுதி வழியாகவோ அல்லது இ-சேவை மையம் வழியாகவோ ஆன்லைனில் விண்ணப்பிக்கலாம். அனைத்து குடும்ப உறுப்பினர்களின் ஆதார் அட்டை, இருப்பிடச் சான்று மற்றும் குடும்பத் தலைவரின் புகைப்படம் தேவை.',
        actions: [
          { label_en: 'Apply for New Smart Card', label_ta: 'புதிய ஸ்மார்ட் கார்டு விண்ணப்பிக்க', url: 'https://www.tnpds.gov.in/pages/newsmartcard' }
        ]
      },
      {
        question: 'How do I check my Smart Card application status?',
        question_ta: 'ஸ்மார்ட் கார்டு விண்ணப்பத்தின் நிலையை எவ்வாறு சரிபார்ப்பது?',
        answer: 'Visit the TNPDS portal and select "Smart Card Application Status". Enter the reference number received via SMS upon registration to track your application stage (Submitted, Field Inspection, Approval, or Card Printing).',
        answer_ta: 'TNPDS இணையதளத்திற்குச் சென்று "ஸ்மார்ட் கார்டு விண்ணப்ப நிலை" என்பதைத் தேர்ந்தெடுக்கவும். பதிவு செய்யும் போது SMS மூலம் பெறப்பட்ட குறிப்பு எண்ணை உள்ளிட்டு தற்போதைய நிலையை அறியலாம்.',
        actions: [
          { label_en: 'Check Application Status', label_ta: 'விண்ணப்ப நிலையை அறிய', url: 'https://www.tnpds.gov.in/pages/smartcard-status' }
        ]
      },
      {
        question: 'How do I add a family member or newborn child to my Ration Card?',
        question_ta: 'குடும்ப அட்டையில் புதிய உறுப்பினரை எவ்வாறு சேர்ப்பது?',
        answer: 'Log in to the TNPDS portal with your registered mobile number and OTP. Go to "Smart Card Services" -> "Add Member". Upload the Birth Certificate for children below 5 years, or Aadhaar card and Marriage Certificate / Parent Card Name Removal Certificate for adults.',
        answer_ta: 'TNPDS தளத்தில் பதிவு செய்யப்பட்ட மொபைல் எண் மற்றும் OTP மூலம் உள்நுழையவும். "ஸ்மார்ட் கார்டு சேவைகள்" -> "உறுப்பினரைச் சேர்க்க" பகுதிக்குச் சென்று, விவரங்களை உள்ளிட்டு பிறப்புச் சான்றிதழ் (5 வயதுக்குட்பட்ட குழந்தைகளுக்கு) அல்லது ஆதார் அட்டை சமர்ப்பிக்கவும்.',
        actions: [
          { label_en: 'Citizen Login to Add Member', label_ta: 'உறுப்பினர் சேர்க்க உள்நுழைக', url: 'https://www.tnpds.gov.in/auth/login' }
        ]
      },
      {
        question: 'How do I remove a family member from my Ration Card?',
        question_ta: 'குடும்ப அட்டையிலிருந்து உறுப்பினரை எவ்வாறு நீக்குவது?',
        answer: 'Log in to the TNPDS portal with your OTP, choose "Smart Card Services" -> "Remove Member", select the person, specify the reason (Marriage, Demise, or Relocation), and upload supporting proof such as a Marriage Certificate or Death Certificate.',
        answer_ta: 'TNPDS தளத்தில் உள்நுழைந்து "ஸ்மார்ட் கார்டு சேவைகள்" -> "உறுப்பினரை நீக்க" என்பதைத் தேர்ந்தெடுக்கவும். உறுப்பினரைத் தேர்ந்தெடுத்து, காரணத்தைக் குறிப்பிட்டு (திருமணம்/மறைவு) அதற்கான சான்றிதழைப் பதிவேற்றவும்.',
        actions: [
          { label_en: 'Citizen Login to Remove Member', label_ta: 'உறுப்பினர் நீக்க உள்நுழைக', url: 'https://www.tnpds.gov.in/auth/login' }
        ]
      },
      {
        question: 'How do I change my address in my Ration Card and transfer shops?',
        question_ta: 'குடும்ப அட்டையில் முகவரியை எவ்வாறு மாற்றுவது?',
        answer: 'Log in to the TNPDS portal and select "Smart Card Services" -> "Change Address". Enter your new residential address and upload valid address proof (Electricity bill, LPG connection receipt, or Registered Rental Deed). Following Taluk Supply Officer (TSO) verification, your assigned Fair Price Shop will automatically update.',
        answer_ta: 'TNPDS இணையதளத்தில் "ஸ்மார்ட் கார்டு சேவைகள்" -> "முகவரி மாற்றம்" என்பதைத் தேர்வு செய்யவும். புதிய முகவரியை உள்ளிட்டு மின் கட்டண ரசீது, எரிவாயு ரசீது அல்லது வாடகை ஒப்பந்தப் பத்திரத்தைப் பதிவேற்றவும். TSO சரிபார்ப்பிற்குப் பிறகு புதிய கடை ஒதுக்கப்படும்.',
        actions: [
          { label_en: 'Change Address Online', label_ta: 'முகவரி மாற்றம் செய்ய', url: 'https://www.tnpds.gov.in/auth/login' }
        ]
      },
      {
        question: 'How do I change the Head of Family in my Ration Card?',
        question_ta: 'குடும்பத் தலைவரை எவ்வாறு மாற்றுவது?',
        answer: 'Log in to the TNPDS portal with OTP and choose "Change Head of Family". Select an existing family member (such as designating the senior female member as head or updating following the demise of the prior head) and upload supporting identification.',
        answer_ta: 'TNPDS தளத்தில் உள்நுழைந்து "குடும்பத் தலைவர் மாற்றம்" என்பதைத் தேர்வு செய்து, உள்ள குடும்ப உறுப்பினர்களில் ஒருவரைத் தேர்வு செய்து தேவையான ஆவணத்தைப் பதிவேற்றவும்.',
        actions: [
          { label_en: 'Change Head of Family', label_ta: 'குடும்பத் தலைவர் மாற்றம் செய்ய', url: 'https://www.tnpds.gov.in/auth/login' }
        ]
      },
      {
        question: 'What are the different types of ration cards in Tamil Nadu?',
        question_ta: 'தமிழ்நாட்டில் உள்ள பல்வேறு வகையான குடும்ப அட்டைகள் யாவை?',
        answer: 'Tamil Nadu has 5 primary smart card categories: 1) PHH (Priority Household / Green Card): Free rice and all subsidized commodities. 2) AAY (Antyodaya Anna Yojana): 35 kg free rice and all items. 3) NPHH (Non-Priority / White Card): Free rice and subsidized items. 4) NPHH-S (Sugar Option): Extra 3 kg sugar in lieu of rice. 5) NPHH-NC (No Commodity): ID and address proof only. Departmental cards include Police (Khaki), Prison, and Forest cards.',
        answer_ta: 'தமிழ்நாட்டில் 5 முக்கிய குடும்ப அட்டை வகைகள் உள்ளன: 1) PHH (முன்னுரிமை / பச்சை): இலவச அரிசி + அனைத்து மானியப் பொருட்கள். 2) AAY (அந்தியோதயா): 35 கிலோ இலவச அரிசி + பொருட்கள். 3) NPHH (வெள்ளை): இலவச அரிசி + பொருட்கள். 4) NPHH-S (சர்க்கரை அட்டை): அரிசிக்கு பதிலாக கூடுதல் 3 கிலோ சர்க்கரை. 5) NPHH-NC (பொருளற்ற அட்டை): அடையாள சான்று மட்டுமே. காவல் துறை, சிறை மற்றும் வனத்துறை அட்டைகளும் உள்ளன.',
        actions: [
          { label_en: 'About Family Cards', label_ta: 'குடும்ப அட்டைகள் பற்றி', url: 'https://www.tnpds.gov.in/pages/familycards' }
        ]
      },
      {
        question: 'Who is eligible for an AAY (Antyodaya Anna Yojana) card?',
        question_ta: 'அந்தியோதயா அன்ன யோஜனா (AAY) அட்டைக்கு யார் தகுதியுடையவர்கள்?',
        answer: 'AAY cards are issued to the poorest and most vulnerable households: destitute individuals, widows, disabled persons living alone, landless agricultural labourers, and rural artisans. Each AAY card receives 35 kg of free rice per month regardless of family size.',
        answer_ta: 'மிகவும் நலிவடைந்த ஏழைக் குடும்பங்கள், ஆதரவற்றோர், விதவைகள், மாற்றுத்திறனாளிகள், நிலமற்ற விவசாயக் கூலித் தொழிலாளர்கள் மற்றும் பழங்குடியினருக்கு AAY அட்டை வழங்கப்படுகிறது. குடும்ப உறுப்பினர் எண்ணிக்கை எதுவாக இருந்தாலும் மாதம் 35 கிலோ அரிசி இலவசமாக வழங்கப்படும்.'
      },
      {
        question: 'How do I switch my card type (Rice ↔ Sugar)?',
        question_ta: 'அட்டை வகையை (அரிசி ↔ சர்க்கரை) எவ்வாறு மாற்றுவது?',
        answer: 'Log in to the TNPDS portal -> "Smart Card Services" -> "Card Type Change". Conversion is allowed from higher-subsidy categories to lower-subsidy options (e.g. Rice Card -> Sugar Card -> No Commodity). Upward conversions require field verification by the Taluk Supply Officer.',
        answer_ta: 'TNPDS தளத்தில் உள்நுழைந்து "ஸ்மார்ட் கார்டு சேவைகள்" -> "அட்டை வகை மாற்றம்" என்பதைத் தேர்வு செய்யவும். அரிசி அட்டையிலிருந்து சர்க்கரை அட்டை அல்லது பொருளற்ற அட்டைக்கு மாற்றலாம்.',
        actions: [
          { label_en: 'Change Card Type', label_ta: 'அட்டை வகை மாற்றம்', url: 'https://www.tnpds.gov.in/auth/login' }
        ]
      },
      {
        question: 'What is the monthly scale of supply and commodity prices?',
        question_ta: 'மாதாந்திர பொருட்கள் ஒதுக்கீடு அளவு மற்றும் விலைகள் என்ன?',
        answer: 'Monthly quota: 1) Rice: Free (Adults: 4 kg, Children: 2 kg; minimum 12 kg, maximum 20 kg per card; AAY: 35 kg). 2) Sugar: ₹25/kg (500g per person, max 2 kg; +3 kg for Sugar cards). 3) Toor Dal: ₹30/kg (1 kg per card). 4) Palmolein Oil: ₹25/packet (1 Litre per card). 5) Wheat: Free (up to 10 kg in Chennai/district HQs, 5 kg in other areas). 6) Kerosene: Allotted based on linked LPG cylinders.',
        answer_ta: '1) அரிசி: இலவசம் (பெரியவர்: 4 கிலோ, குழந்தை: 2 கிலோ; குறைந்தபட்சம் 12 கிலோ, அதிகபட்சம் 20 கிலோ; AAY: 35 கிலோ). 2) சர்க்கரை: ₹25/கிலோ (நபருக்கு 500 கிராம்). 3) துவரம் பருப்பு: ₹30/கிலோ (1 கிலோ). 4) பாமாயில்: ₹25/பாக்கெட் (1 லிட்டர்). 5) கோதுமை: இலவசம் (5-10 கிலோ). 6) மண்ணெண்ணெய்: சிலிண்டர் எண்ணிக்கையின் அடிப்படையில் ஒதுக்கப்படும்.'
      },
      {
        question: 'Who is eligible for PDS kerosene?',
        question_ta: 'நியாய விலைக் கடையில் மண்ணெண்ணெய் பெற யார் தகுதியுடையவர்கள்?',
        answer: 'Kerosene quota is determined strictly by the number of registered LPG cooking gas cylinders linked to your card: 0 LPG cylinders receive 3 to 15 Litres (district-dependent); 1 LPG cylinder receives 3 Litres; cards with 2 active LPG cylinders are not eligible for kerosene.',
        answer_ta: 'குடும்ப அட்டையுடன் இணைக்கப்பட்டுள்ள எரிவாயு சிலிண்டர்களின் எண்ணிக்கையின் அடிப்படையில் மண்ணெண்ணெய் வழங்கப்படுகிறது: சிலிண்டர் இல்லாதவர்களுக்கு 3 முதல் 15 லிட்டர்; 1 சிலிண்டர் உள்ளவர்களுக்கு 3 லிட்டர்; 2 சிலிண்டர்கள் உள்ளவர்களுக்கு மண்ணெண்ணெய் கிடையாது.'
      },
      {
        question: 'How do I file a complaint against a Fair Price Shop?',
        question_ta: 'நியாய விலைக் கடை மீது புகார் அளிப்பது எப்படி?',
        answer: 'You can register a complaint via 3 official channels: 1) Call Toll-Free 1967 or 1800-425-5901. 2) Submit a complaint online at the TNPDS portal ("Register a Complaint") to obtain a tracking number. 3) Contact your local Taluk Supply Officer (TSO) or District Supply Officer (DSO).',
        answer_ta: 'புகார் அளிக்க 3 வழிகள் உள்ளன: 1) கட்டணமில்லா உதவி எண் 1967 அல்லது 1800-425-5901 ஐ அழைக்கவும். 2) TNPDS தளத்தில் "புகார் பதிவு செய்ய" பகுதி வழியாக ஆன்லைனில் பதிவு செய்யலாம். 3) உங்கள் உள்ளூர் வட்ட வழங்கல் அலுவலர் (TSO) அல்லது மாவட்ட வழங்கல் அலுவலரை (DSO) தொடர்புகொள்ளலாம்.',
        actions: [
          { label_en: 'Register Complaint Online', label_ta: 'ஆன்லைனில் புகார் அளிக்க', url: 'https://www.tnpds.gov.in/pages/feedback' }
        ]
      },
      {
        question: 'Can I collect rations from any shop across Tamil Nadu (ONORC Portability)?',
        question_ta: 'தமிழ்நாட்டில் உள்ள எந்த கடையிலும் ரேஷன் பொருட்களை வாங்க முடியுமா?',
        answer: 'Yes! Under the One Nation One Ration Card (ONORC) and statewide portability framework, any smart card holder in Tamil Nadu can collect their monthly ration entitlement from ANY Fair Price Shop across the state using Aadhaar biometric authentication.',
        answer_ta: 'ஆம்! "ஒரே நாடு ஒரே ரேஷன் கார்டு" (ONORC) திட்டத்தின் கீழ், தமிழ்நாட்டின் எந்தவொரு குடும்ப அட்டைதாரரும் மாநிலத்தின் எந்த ஒரு நியாய விலைக் கடையிலும் பயோமெட்ரிக் ஆதார் கைரேகை மூலம் தனது மாதாந்திர ரேஷன் பொருட்களைப் பெறலாம்.'
      }
    ],
    ctaText: 'Search by Shop Number / Code',
    alertTitle: 'Got a Ration Shop Code?',
    alertText: 'Click the Search by Shop Number / Code button above to open our interactive map and search for your exact shop address instantly.'
  },
  TNEB: {
    layer: 'TNEB',
    title: 'TNEB Section Office & Jurisdiction Finder',
    summary: 'Identify precise electricity board section boundaries and retrieve official office details for your Tamil Nadu neighborhood.',
    inDepthOverview: 'Power management across Tamil Nadu is divided into highly specific Section Office grids governed by TNEB (TANGEDCO). Knowing your correct Section is vital for escalating power outages, applying for new service connections, or filing billing complaints. This module utilizes spatial jurisdiction polygons to pinpoint exactly which Assistant Engineer (AE) office oversees a specific plot of land. By integrating these invisible borders onto a digital canvas, NammaMap removes the guesswork often associated with utility escalations, giving both consumers and technicians immediate clarity.',
    whyItMatters: [
      'Dramatically reduces escalation delays by routing you to the correct jurisdiction instantly.',
      'Provides visual clarity on complex electrical grid perimeters that often bisect streets.',
      'Supports infrastructure maintenance crews in assessing spatial service responsibility.',
      'Assists in identifying responsible contact zones for heavy industrial power inquiries.'
    ],
    howToUse: [
      'Switch the active layer to TNEB and zoom to your specific dwelling or commercial location.',
      'Tap the land area to reveal the governing electricity board section name and boundaries.',
      'View the detailed sidebar listing for administrative contact cues if populated.',
      'Copy the precise coordinate link when submitting digital maintenance request tickets.'
    ],
    useCases: [
      'Complaint escalation for power supply interruptions or utility pole hazards.',
      'New electricity connection application workflows requiring correct office designation.',
      'Emergency service response coordination for regional electrical incidents.'
    ],
    faq: [
      {
        question: 'Will this map help me find my Assistant Engineer office?',
        answer: 'Yes. The primary focus of the TNEB module is to map the governing Section Office boundaries typically administered by the local Assistant Engineer.'
      },
      {
        question: 'Are section boundaries different from regular revenue districts?',
        answer: 'Often, yes. Electrical sections are drawn based on network distribution, meaning they frequently do not follow standard district or ward lines.'
      },
      {
        question: 'Can I use this to lookup commercial supply zones?',
        answer: 'The jurisdiction polygons apply to all consumer categories served by the respective grid infrastructure within that perimeter.'
      },
      {
        question: 'Is my current bill applicable to the highlighted office?',
        answer: 'Your monthly TNEB bill displays a section code that should directly correspond to the geographic office displayed in this GIS finder.'
      }
    ],
    ctaText: 'Find your TNEB Section',
    alertTitle: 'Need your Electricity Board Details?',
    alertText: 'Click the Find your TNEB Section button above to open the map and tap on your street to instantly identify your local AE office.'
  },
  CONSTITUENCY: {
    layer: 'CONSTITUENCY',
    title: 'Assembly & Parliamentary Constituency Map',
    summary: 'Explore Legislative Assembly (AC) and Parliamentary (PC) boundary limits for Tamil Nadu with clear geographic precision.',
    inDepthOverview: 'Political boundaries are essential pillars of democratic participation. Understanding whether your residence falls under a particular Legislative Assembly or Parliamentary seat can be challenging near boundary fringes. The Constituency Module offers an intuitive switch between State Assembly maps and national Lok Sabha maps. Designed for both citizens aiming to locate their voting zones and policy analysts tracking demographic layouts, this engine overlays ECI-sourced delimitation data ensuring full confidence in regional boundary classification.',
    whyItMatters: [
      'Provides instant clarity regarding voter registration areas and voting booth alignment.',
      'Allows civic organizations to execute accurate field operations tailored to specific seats.',
      'Enables precise analysis of geographic representation during policy debates.',
      'Eliminates confusion for citizens living near overlapping border districts.'
    ],
    howToUse: [
      'Select Constituencies and toggle the submenu between "Assembly" and "Parliament".',
      'Search by the name of the candidate seat or utilize your pincode to locate your region.',
      'Review the resulting bold boundary highlighted to witness the entire electoral footprint.',
      'Utilize spatial filtering to examine adjoining seats and overlapping regional networks.'
    ],
    useCases: [
      'Voter awareness campaigns tracking demographic reach inside specific limits.',
      'Academic and electoral research regarding spatial seat layouts across the state.',
      'Determining specific representative responsibilities for civic petitions.'
    ],
    faq: [
      {
        question: 'Can I see both Assembly (MLA) and Parliament (MP) boundaries?',
        answer: 'Yes. Use the specialized toggle in the sidebar to shift seamlessly between state-level AC and national-level PC layouts.'
      },
      {
        question: 'Is recent election data integrated into these maps?',
        answer: 'The core functionality displays spatial boundaries; where enabled by feature flags, live or historical result dashboards overlay these zones.'
      },
      {
        question: 'Do constituency borders align perfectly with district borders?',
        answer: 'Not always. Electoral delimitation focuses on population size, meaning a single constituency can sometimes straddle multiple districts.'
      },
      {
        question: 'How frequently are these boundary polygons updated?',
        answer: 'Polygons reflect the latest official Election Commission delimitation. Updates are made whenever official federal reorganization occurs.'
      }
    ],
    ctaText: 'Search by Assembly / Parliament',
    alertTitle: 'Looking for your Constituency?',
    alertText: 'Click the Search button above to open the interactive map and pinpoint your exact voting boundary.'
  },
  POLICE: {
    layer: 'POLICE',
    title: 'Police Station Jurisdiction Locator',
    summary: 'Locate police stations and identify responsible jurisdictional boundaries for emergency planning and official reporting.',
    inDepthOverview: 'When reporting incidents or establishing jurisdictional responsibility, knowing the exact coverage area of a Local Police Station is paramount. The Police Jurisdiction map removes navigational uncertainty by illustrating the actual geographic perimeter assigned to each station across Tamil Nadu. Moving beyond simple "nearest station" distance calculations, this GIS engine informs you who officially handles your sector, which is indispensable for filing FIRs, seeking local clearance, or planning logistical safety protocols.',
    whyItMatters: [
      'Ensures accurate filing of documentation with the correct local police outpost.',
      'Reduces critical time delays encountered when citizens unknowingly visit the wrong jurisdiction.',
      'Assists legal firms and civic response units in pinpointing local authority grids.',
      'Enhances transparency surrounding localized public safety coverage zones.'
    ],
    howToUse: [
      'Enter the Police module and input your address, station name, or area landmark.',
      'View the designated markers detailing location points for the active stations.',
      'Click boundaries to reveal which station covers that specific street network.',
      'Extract deep links to quickly inform safety personnel of your exact responsible precinct.'
    ],
    useCases: [
      'Filing non-emergency complaints and conducting verification check-ins.',
      'Route safety analysis and establishing logistical transport check-points.',
      'Identifying administrative responsibility for event permissions and localized security.'
    ],
    faq: [
      {
        question: 'Can I find a specific police station by its official code?',
        answer: 'Yes. The indexing system accounts for standard station identification, letting you locate the physical footprint by typing its code.'
      },
      {
        question: 'Does the map cover every single district in Tamil Nadu?',
        answer: 'The data captures statewide footprints, aiming to represent jurisdictional maps for all active commissionerate and district ranges.'
      },
      {
        question: 'What should I do if I am on the exact border of two stations?',
        answer: 'The polygons define strict boundaries. If near a border, cross-reference exact coordinates using the share feature for clarity.'
      },
      {
        question: 'Are all-women police stations included in this view?',
        answer: 'Primary jurisdiction outlines cover territorial precincts; specialty stations often appear as localized points within the general view.'
      }
    ],
    ctaText: 'Find your Police Station',
    alertTitle: 'Need your Local Police Jurisdiction?',
    alertText: 'Click the Find your Police Station button above to open our map and discover exactly which station covers your area.'
  },
  HEALTH: {
    layer: 'HEALTH',
    title: 'Health Facility & Hospital Map for Tamil Nadu',
    summary: 'Explore public health infrastructure, primary health centers, and district health access tiers across the Tamil Nadu region.',
    inDepthOverview: 'Modern healthcare planning relies on understanding proximity and tiered access. The Tamil Nadu Health Facility map inventories government healthcare units, spanning high-capacity District Hospitals down to local Primary Health Centers (PHCs). Citizens can utilize this geospatial view to assess regional capacity, identify the closest government triage centers, and filter resources based on specific care capabilities. This transparency supports rapid patient routing and aids researchers tracking the density of medical care in rural vs urban municipalities.',
    whyItMatters: [
      'Assists citizens in quickly identifying the closest available public healthcare asset.',
      'Empowers public health researchers to visualize gaps in regional care density.',
      'Allows community workers to filter facility capability hierarchies (PHC, District, Specialty).',
      'Improves emergency preparedness by cataloging verified governmental medical infrastructure.'
    ],
    howToUse: [
      'Navigate to the Health module and execute a search using your current district or pincode.',
      'Use advanced filtering widgets to narrow findings by facility type or service level.',
      'Select individual facility nodes to review structural identifiers and exact location markers.',
      'Generate shareable links to assist incoming travelers or emergency callers in navigation.'
    ],
    useCases: [
      'Locating tier-1 medical support for non-critical government services.',
      'Executing public healthcare access assessments for grant or municipal analysis.',
      'Directing community outreach teams to central facility hub coordinates.'
    ],
    faq: [
      {
        question: 'Does the map distinguish between PHCs and larger hospitals?',
        answer: 'Yes. The module supports tiered metadata classification, allowing you to separate primary centers from secondary and tertiary units.'
      },
      {
        question: 'Can I find health facilities specific to a single pincode?',
        answer: 'Absolutely. Pincode spatial resolution extracts only relevant assets physically operating inside that specific postal boundary.'
      },
      {
        question: 'Is contact info available for every listed medical center?',
        answer: 'Currently, the focus is spatial mapping and facility classification; address details appear when available in official datasets.'
      },
      {
        question: 'Can I filter facilities by District or Block?',
        answer: 'Yes. Selecting a district generates a scoped view showing all verified assets clustered specifically inside that revenue zone.'
      }
    ],
    ctaText: 'Find Local Hospitals & PHCs',
    alertTitle: 'Looking for a Government Hospital?',
    alertText: 'Click the Find Local Hospitals button above to open the map and discover healthcare facilities near you.'
  },
  LOCAL_BODIES_V2: {
    layer: 'LOCAL_BODIES_V2',
    title: 'Local Bodies Map: Corporations, Municipalities, Panchayats',
    summary: 'Analyze administrative local governance perimeters, covering municipal corporations, town panchayats, and revenue block divisions.',
    inDepthOverview: 'Governing infrastructure in Tamil Nadu consists of intricate layers including Municipal Corporations, Municipalities, and Town Panchayats. Determining which administrative body taxes, licenses, and maintains your street requires concrete geographic boundaries. The Local Bodies module catalogs these governance shells, simplifying navigation for planners, business owners, and citizens. By rendering strict administrative perimeters, NammaMap makes it effortless to ensure you are coordinating with the correct municipal authority.',
    whyItMatters: [
      'Clarifies which civic body handles local taxation, infrastructure, and sanitation.',
      'Aids businesses in identifying proper licensing jurisdictions for expansion.',
      'Eliminates border confusion between expanding town panchayats and central municipalities.',
      'Fosters greater awareness of localized administrative responsibilities.'
    ],
    howToUse: [
      'Switch the dashboard to Local Bodies and zoom to your intended inquiry point.',
      'Observe the dynamic polygon highlights identifying governance tiers.',
      'Tap to select an active polygon and view the underlying administrative metadata card.',
      'Cross-verify neighboring bodies to observe municipal expansion vectors.'
    ],
    useCases: [
      'Urban development planning and localized infrastructure zoning.',
      'Determining proper channels for trade licenses, tax filings, and building permits.',
      'Facilitating civic participation and direct contact with local governing boards.'
    ],
    faq: [
      {
        question: 'Which specific tiers of local governance are mapped?',
        answer: 'The module includes Corporations, Municipalities, and varying levels of Panchayat bodies depending on data availability.'
      },
      {
        question: 'Will I see specific ward numbers inside this view?',
        answer: 'General release focuses on overall local body boundaries; sub-ward boundaries are introduced via secondary filters where accessible.'
      },
      {
        question: 'How does this help with paying my local property tax?',
        answer: 'By knowing your exact Municipal or Corporation jurisdiction, you can navigate directly to the correct payment portal for your region.'
      },
      {
        question: 'Can I search for a local body by its direct name?',
        answer: 'Yes. Enter the specific name of the Municipality or Panchayat into the search to immediately isolate its geographic footprint.'
      }
    ],
    ctaText: 'Find your Local Body (Corporation/Municipality)',
    alertTitle: 'Which Municipality handles your area?',
    alertText: 'Click the Find your Local Body button above to locate your exact civic administrative boundary.'
  },
  VETERINARY: {
    layer: 'VETERINARY',
    title: 'Veterinary Facilities Map for Tamil Nadu',
    summary: 'Identify government veterinary hospitals, dispensaries, and animal clinics to secure tiered care for pets and agricultural livestock.',
    inDepthOverview: 'Veterinary support is crucial to both urban pet owners and rural livestock economies. Accessing government-run animal husbandry services shouldn’t require difficult inquiries. The Tamil Nadu Veterinary map charts the statewide network of animal care assets, grouping them into their respective operational tiers: Poly Clinics, Hospitals, Dispensaries, and Sub-Centers. Whether navigating a high-priority livestock emergency or scheduling routine vaccinations, this spatial directory connects owners to legitimate state care points fast.',
    whyItMatters: [
      'Accelerates emergency access for critical livestock and poultry medical incidents.',
      'Assists rural farmers in locating their designated veterinary sub-center networks.',
      'Simplifies care navigation for household pet owners seeking localized clinic assets.',
      'Supports visual planning for vaccination drives and agricultural support outreach.'
    ],
    howToUse: [
      'Open the Veterinary layer and utilize regional zoom levels to identify your village.',
      'Click cluster markers to explode individual dispensaries or clinics on the viewport.',
      'Select a pin to extract facility classification data and official name registry.',
      'Share deep links directly from the app for efficient on-the-ground navigation.'
    ],
    useCases: [
      'Farming emergencies requiring immediate transport to the nearest Veterinary Hospital.',
      'Planning routine veterinary care and vaccination support at the village level.',
      'Field service coordination for Veterinary surgeons and staff networks.'
    ],
    faq: [
      {
        question: 'What are the differences between Poly Clinics and Dispensaries?',
        answer: 'Poly Clinics and Hospitals generally offer high-tier critical care and diagnostics, whereas Dispensaries handle local routine treatments.'
      },
      {
        question: 'Can I find deep links for a specific veterinary clinic?',
        answer: 'Yes. Every facility supports direct key generation allowing for precise shareable deep links like /veterinary/{village_taluk}.'
      },
      {
        question: 'Does the map cover rural villages or just main cities?',
        answer: 'The module features rigorous state-wide indexing, including hundreds of small-scale rural sub-centers supporting agriculture.'
      },
      {
        question: 'Are emergency contact phone numbers available on the markers?',
        answer: 'Currently, maps facilitate physical coordinates and registry naming; always coordinate final arrival with local department staff.'
      }
    ],
    ctaText: 'Find Veterinary Hospitals',
    alertTitle: 'Need Government Animal Care?',
    alertText: 'Click the Find Veterinary Hospitals button above to open our interactive map and locate nearby clinics and dispensaries.'
  },
  REGISTRATION: {
    layer: 'REGISTRATION',
    title: 'Tamil Nadu Sub-Registrar Office (SRO) Boundaries & Jurisdictions',
    summary: 'Identify the official Tamil Nadu Sub-Registrar Office boundaries, locate SRO offices, and verify jurisdictional coverage for property registration and legal documentation.',
    inDepthOverview: 'Property transactions and legal documentation in Tamil Nadu are strictly governed by Sub-Registrar Office (SRO) boundaries. Each SRO has a defined geographic jurisdiction consisting of specific revenue villages. Knowing the exact "Tamil Nadu Sub-Registrar Office boundaries" that cover your property is essential for legal validity. The Registration Module maps these critical jurisdictions, allowing citizens, builders, and legal professionals to instantly identify the correct office responsible for any land parcel in the state.',
    whyItMatters: [
      'Ensures legal documents are filed at the correct jurisdictional office.',
      'Prevents delays in property registration by identifying the responsible SRO beforehand.',
      'Aids legal professionals in verifying territorial jurisdiction for document drafting.',
      'Enhances transparency in administrative boundaries for citizens and businesses.'
    ],
    howToUse: [
      'Open the Registration module and click anywhere on the map to find the governing SRO.',
      'The map will highlight the entire jurisdictional boundary of the responsible office.',
      'Check the result card for office codes, zone names, and revenue village details.',
      'Use the Get Directions button to navigate to the physical office location.'
    ],
    useCases: [
      'Property sale, purchase, or lease registration planning.',
      'Marriage registration jurisdictional verification.',
      'Legal research for territorial jurisdiction in civil documentation.'
    ],
    faq: [
      {
        question: 'How do I know which SRO covers my property?',
        answer: 'Simply click your property location on the map in the Registration layer. The system will highlight the boundary and display the responsible office.'
      },
      {
        question: 'Is the SRO code displayed on the map?',
        answer: 'Yes. The result card shows the official SRO Code, which is useful for filling out online registration forms on the STAR 2.0 portal.'
      },
      {
        question: 'Can an SRO jurisdiction cover multiple districts?',
        answer: 'Generally, SROs are nested within Registration Districts, which may differ slightly from Revenue Districts. The map reflects these official registration boundaries.'
      },
      {
        question: 'Are the physical office locations exact?',
        answer: 'The office markers represent the physical location of the SRO. Always verify current office addresses on the official Registration Department website as offices may occasionally relocate.'
      }
    ],
    ctaText: 'Find your Sub-Registrar Office (SRO)',
    alertTitle: 'Registering a Property?',
    alertText: 'Click the Find your SRO button above to open the map and tap your location to identify your official Registration boundary.'
  },
  LAND_PARCEL: {
    layer: 'LAND_PARCEL',
    title: 'Survey No. & Land Parcel Finder',
    summary: 'Identify cadastral land parcel boundaries and retrieve survey number details for any location in Tamil Nadu.',
    inDepthOverview: 'Understanding land boundaries and survey numbers is the first step in any property verification or civil planning. The Survey No. Finder provides an interactive gateway to cadastral data, enabling users to click anywhere on the map to resolve the underlying land parcel. This module extracts precise survey numbers, subdivisions, and ULPIN identifiers. By integrating these intricate boundaries with satellite and terrain views, NammaMap offers an intuitive way for citizens to cross-reference physical land with official records.',
    whyItMatters: [
      'Provides instant identification of survey numbers and subdivision codes for any plot.',
      'Helps in verifying the official village and taluk classification of a land parcel.',
      'Aids in preparing for property registration, land surveys, and legal documentation.',
      'Enhances transparency by providing indicative cadastral boundaries on an interactive map.'
    ],
    howToUse: [
      'Switch to the Survey No. Finder layer and zoom into your area of interest.',
      'Click any point on the map to trigger a lookup of the cadastral data.',
      'Observe the highlighted parcel boundary and inspect the details in the result card.',
      'Copy the survey number or share the direct link to the parcel for coordination.'
    ],
    useCases: [
      'Property verification during sale, purchase, or mortgage processes.',
      'Assisting land surveyors and civil engineers in identifying field perimeters.',
      'Revenue administration research for taluk and village-level land distribution.'
    ],
    faq: [
      {
        question: 'Are the boundaries displayed here legally binding?',
        answer: 'No. The boundaries are indicative and sourced from TNGIS. For official legal boundary verification, always obtain a certified Field Measurement Sketch (FMB) from the Revenue Department.'
      },
      {
        question: 'What is a ULPIN?',
        answer: 'The Unique Land Parcel Identification Number (ULPIN) is a 14-digit alphanumeric code that serves as the "Aadhaar for land", uniquely identifying every surveyed parcel of land in India.'
      },
      {
        question: 'Can I search by survey number directly?',
        answer: 'Currently, the module supports click-to-resolve functionality. You can click on the map to find the survey number for that exact geographic location.'
      }
    ],
    ctaText: 'Search by Survey Number / Location',
    alertTitle: 'Need Land Parcel Details?',
    alertText: 'Click the Search by Survey Number button above to open the cadastral map and inspect specific land subdivision boundaries.'
  },
  TEMPLES: {
    layer: 'TEMPLES',
    title: 'Explore Temples in Tamil Nadu: District-wise Cultural Heritage Map',
    summary: 'Locate historical and local temples, view deity information, timings, facilities, puja timings, and heritage site designations.',
    inDepthOverview: 'Tamil Nadu is home to a rich repository of culture, architecture, and spirituality, often designated under culture and heritage mapping. The Temples module catalogs these historic and sacred places across all districts. Rather than relying on simple coordinates, NammaMap provides comprehensive metadata for each temple, including primary Deity and Goddess details, timings for public access, puja schedules, on-site facilities, and heritage badges. This module operates using a progressive boundary system: clicking on the map or entering a pincode resolves the target district and loads all temples within that zone, rendering them as markers to ensure fast loading and responsive interactions.',
    whyItMatters: [
      'Provides quick access to darshan, puja, and operational timings for devotees.',
      'Identifies visitor facilities such as drinking water, parking, and administrative offices.',
      'Highlights key architectural features, sanctorum shapes, and heritage classifications.',
      'Allows tourists and pilgrims to plan travel itineraries across Tamil Nadu districts.'
    ],
    howToUse: [
      'Switch the active layer to Temples under the Culture & Heritage category.',
      'Search for a temple by name or click within a pincode area to load the district\'s temples.',
      'Notice the markers indicating locations of verified temples in the highlighted region.',
      'Select a marker to slide open the detail card containing deities, schedules, and badges.',
      'Copy the link to share the direct view of any temple with friends or family.'
    ],
    useCases: [
      'Planning pilgrimage routes and local travel itineraries.',
      'Verifying specific temple schedules for festivals and daily pujas.',
      'Assisting heritage researchers in identifying historically marked sites.'
    ],
    faq: [
      {
        question: 'Are all temples in Tamil Nadu mapped in this module?',
        answer: 'This module houses a major collection of public and historical temples classified by district. The registry continues to grow as verified records are integrated.'
      },
      {
        question: 'What do the Heritage and Kattalai badges mean?',
        answer: 'Heritage badges indicate structures of historical or architectural significance. Kattalai badges designate temples with specific endowment endowments or services.'
      },
      {
        question: 'Can I find a temple using its unique ID?',
        answer: 'Yes. Deep linking supports unique temple keys, enabling you to load the map and center directly on a specific temple.'
      },
      {
        question: 'Is the data loaded dynamically to save mobile bandwidth?',
        answer: 'Absolutely. Temples are loaded progressively on-demand when you query a district or pincode, ensuring optimal performance on all devices.'
      }
    ],
    ctaText: 'Explore District Temples',
    alertTitle: 'Planning a Temple Visit?',
    alertText: 'Click the Explore District Temples button above to open the map and find heritage sites and timings.'
  },
  RADIO: {
    layer: 'RADIO',
    title: 'Akashvani FM Radio Stations',
    summary: 'Listen to local Akashvani FM radio broadcasts across Tamil Nadu.',
    inDepthOverview: 'The Radio module offers a unique way to tune in to various Akashvani FM broadcasts from all over Tamil Nadu directly through the NammaMap interface.',
    whyItMatters: ['Provides easy access to local news and cultural broadcasts.', 'Supports audio integration with regional spatial data.'],
    howToUse: ['Select the Akashvani FM layer to see radio station coverage.', 'Click on a station to view now playing data and listen to the stream.'],
    useCases: ['Cultural exploration.', 'Tuning into local events.'],
    faq: [{ question: 'Is the broadcast live?', answer: 'Yes, it points directly to Prasar Bharati audio streams.' }],
    ctaText: 'Listen to Local FM Radio',
    alertTitle: 'Want to tune in to Akashvani?',
    alertText: 'Click the Listen button above to open the map and select your regional station to start streaming.'
  },
  FUEL: {
    layer: 'FUEL',
    title: 'Petrol Bunks & Fuel Stations in Tamil Nadu',
    summary: 'Locate petrol bunks across Tamil Nadu, filter by oil company and by the fuel grades each outlet actually sells.',
    inDepthOverview: 'The Fuel Outlets module maps retail fuel stations across Tamil Nadu, sourced from the public dealer locators published by the oil marketing companies. Each outlet carries its brand, street address, the fuel grades it stocks and, where the dealer publishes them, contact number and forecourt amenities such as air filling, ATM and 24-hour service. Coverage is strongest for BPCL and Nayara; Jio-bp and Shell are included where their dealer listings expose location data. You can browse statewide, drill into a district, or restrict the view to your own pincode.',
    whyItMatters: [
      'Finds the nearest petrol bunk when travelling through unfamiliar districts.',
      'Shows which outlets stock premium grades rather than only regular petrol and diesel.',
      'Surfaces forecourt amenities such as air filling and 24-hour service before you pull in.',
      'Helps fleet and logistics planning by showing outlet density along a corridor.'
    ],
    howToUse: [
      'Open the Fuel Outlets module to see stations across Tamil Nadu.',
      'Use the scope switch to move between statewide, a single district, and your own pincode.',
      'Filter by oil company or by the fuel type you need; the chips show live counts.',
      'Click any marker for the address, contact number, amenities and directions.'
    ],
    useCases: [
      'Planning refuelling stops on a long drive.',
      'Finding a premium-grade or EV charging outlet nearby.',
      'Checking fuel retail density across a district.'
    ],
    faq: [
      {
        question: 'Are all petrol bunks in Tamil Nadu covered?',
        answer: 'Not yet. Coverage is built from the dealer locators the oil companies publish, so outlets whose brands do not expose location data are missing. Treat the map as a strong guide rather than a complete register.'
      },
      {
        question: 'Are fuel prices shown?',
        answer: 'No. Retail prices change daily and are not published in the dealer listings, so the map shows which grades an outlet stocks rather than what they cost.'
      },
      {
        question: 'What do the fuel filters mean?',
        answer: 'Each brand names its grades differently, so they are grouped into Petrol, Diesel, Premium and EV Charging. Premium covers the higher-octane grades such as Speed and the various premium petrol and diesel products.'
      },
      {
        question: 'How current is the data?',
        answer: 'It is a periodic snapshot of the dealer locators, so a recently opened or closed outlet may lag. Use the report button on any card to flag something that looks wrong.'
      }
    ],
    ctaText: 'Find Petrol Bunks Near You',
    alertTitle: 'Looking for the nearest petrol bunk?',
    alertText: 'Open the map and use My Area to see the fuel stations around your current location.'
  }
};

export const GUIDEABLE_LAYERS: ServiceLayer[] = [
  'PINCODE',
  'PDS',
  'TNEB',
  'CONSTITUENCY',
  'POLICE',
  'HEALTH',
  'LOCAL_BODIES_V2',
  'VETERINARY',
  'REGISTRATION',
  'LAND_PARCEL',
  'TEMPLES',
  'RADIO',
  'FUEL'
];

export const getGuidePath = (layer: ServiceLayer) => `/guides/${getLayerSlug(layer)}`;
